import _ from "lodash";
import { getPlatformUserDetailsFromCache } from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import { ADMIN } from "Constants/stringConstants";
import {
  MODULES,
  RULE_OPTIONS,
  ACCESS_LEVELS,
  STAFF_PERMISSIONS_CONFIGURATIONS,
  MODULE_CONFIGURATION_MAP,
  MODULE_PERMS,
  CONFIG_STAFF_PERM_MAP,
  CURRICULUM_MODULE_MAP,
} from "./staffPermissionMaps";

export {
  MODULES,
  RULE_OPTIONS,
  ACCESS_LEVELS,
  STAFF_PERMISSIONS_CONFIGURATIONS,
  MODULE_CONFIGURATION_MAP,
  MODULE_PERMS,
  CONFIG_STAFF_PERM_MAP,
  CURRICULUM_MODULE_MAP,
};

let store;
import("store/createStore").then(importedModule => {
  store = importedModule.store;
});

export const getIsAdminByRole = () => {
  if (!ACLStore.isStaffPermissionEnabled()) {
    return ACLStore.can("AdminPortal");
  }
  const userRole = _.get(store?.getState() ?? {}, "login.userInfo.role");
  return userRole === ADMIN;
};

export const getAdminInfo = () => {
  if (!ACLStore.isStaffPermissionEnabled()) {
    return ACLStore.can("AdminPortal");
  }
  const userRole = _.get(store?.getState() ?? {}, "login.userInfo");
  return _.get(userRole, "id");
};

export const hasModuleAdminAccess = ({ moduleName }) => {
  const isAdmin = getIsAdminByRole();

  if (isAdmin) {
    return isAdmin;
  }

  if (ACLStore.isStaffPermissionEnabled(moduleName)) {
    const moduleAccessString = ACLStore.getUserModuleAccess(moduleName);
    return moduleAccessString === ACCESS_LEVELS.ADMIN_ACCESS.id;
  }

  return false;
};

export const getDesignationIdFromUserDetails = () => {
  const state = store?.getState() ?? {};
  const { userInfo: { id, userEntityType } = {} } = _.get(state, "login", {});
  const staffDataFromCache = getPlatformUserDetailsFromCache({
    id: id,
    type: userEntityType,
  });
  return _.get(staffDataFromCache, "designation.id");
};

export const getModuleAccessLevels = _.memoize(module => {
  const accessValues = _.entries(MODULES[module].values);
  const valuesDetails = _.map(
    accessValues,
    ([accessKey, accessValue], index) => {
      return {
        ...accessValue,
        displayOrder: index,
        accessOrder: _.size(accessValues) - index,
      };
    }
  );
  return valuesDetails;
}, JSON.stringify);

export const getAccessLevels = (
  perm,
  accessLevel,
  skipAvailiblityCheck = false // to skip module availiblity check in admin portal (for staff permission)
) => {
  const key = _.first(_.keys(perm));

  const features = perm[key];
  const featureKeys = _.keys(features);
  const moduleAccessList = getModuleAccessLevels(key);

  const moduleAccessString = accessLevel || ACLStore.getUserModuleAccess(key);
  const moduleAccessForUser = _.find(
    moduleAccessList,
    accessOption => _.get(accessOption, "id") === moduleAccessString
  );

  const values = _.reduce(
    featureKeys,
    (acc, feature) => {
      const {
        rule,
        value,
        defaultAccess = false,
        defaultAccessForPermissionNotEnabled,
        defaultAccessForUserBeingAdmin,
      } = features[feature];

      const module = skipAvailiblityCheck ? undefined : key;
      acc[feature] = checkModuleAccess({
        moduleAccessForUser,
        rule,
        value,
        moduleAccessList,
        defaultAccess,
        defaultAccessForPermissionNotEnabled,
        defaultAccessForUserBeingAdmin,
        module,
      });

      return acc;
    },
    {}
  );

  return values;
};

export const hasModuleAccess = (menuNode, curriculumId) => {
  const { rolesAndPermission } = menuNode;
  const { moduleName, accessCondition } = rolesAndPermission || {};
  if (!ACLStore.isStaffPermissionEnabled(moduleName)) return true;
  if (!curriculumId) return true;
  if (getIsAdminByRole()) {
    return true;
  }

  if (!rolesAndPermission) return true;

  const module = MODULES[moduleName];

  const accessValues = getAccessLevels({
    [module.id]: {
      allowAccess: { ...accessCondition(module) },
    },
  });

  const { allowAccess } = accessValues;
  return allowAccess;
};

const checkModuleAccess = ({
  moduleAccessForUser,
  rule,
  value,
  moduleAccessList,
  defaultAccess,
  defaultAccessForPermissionNotEnabled,
  defaultAccessForUserBeingAdmin,
  module,
}) => {
  if (!ACLStore.isStaffPermissionEnabled(module))
    return !_.isUndefined(defaultAccessForPermissionNotEnabled)
      ? defaultAccessForPermissionNotEnabled
      : defaultAccess;

  if (getIsAdminByRole()) {
    return !_.isUndefined(defaultAccessForUserBeingAdmin)
      ? defaultAccessForUserBeingAdmin
      : defaultAccess;
  }

  if (!rule || !value) {
    return defaultAccess;
  }

  if (!moduleAccessForUser) return false;

  const requestedComparingModule = _.find(
    moduleAccessList,
    accessOption => _.get(accessOption, "id") === value
  );

  const possibleOptions = getAccessModuleOptionsByRule({
    rule,
    requestedComparingModule,
    moduleAccessList,
  });

  const possibleOptionsIds = _.map(possibleOptions, "id");
  return _.includes(possibleOptionsIds, _.get(moduleAccessForUser, "id"));
};

const getAccessModuleOptionsByRule = ({
  rule,
  requestedComparingModule,
  moduleAccessList,
}) => {
  const { accessOrder } = requestedComparingModule;

  switch (rule) {
    case RULE_OPTIONS.eq:
      return _.filter(
        moduleAccessList,
        access => access.accessOrder === accessOrder
      );
    case RULE_OPTIONS.lte:
      return _.filter(
        moduleAccessList,
        access => access.accessOrder <= accessOrder
      );
    case RULE_OPTIONS.lt:
      return _.filter(
        moduleAccessList,
        access => access.accessOrder < accessOrder
      );
    case RULE_OPTIONS.gt:
      return _.filter(
        moduleAccessList,
        access => access.accessOrder > accessOrder
      );
    case RULE_OPTIONS.gte:
      return _.filter(
        moduleAccessList,
        access => access.accessOrder >= accessOrder
      );
    default:
      return [];
  }
};

export const getIconByAccessLevel = accessLevel => {
  return _.find(ACCESS_LEVELS, level => level.id === accessLevel)?.Icon;
};

export const getLabelByAccessLevel = accessLevel => {
  return _.find(ACCESS_LEVELS, level => level.id === accessLevel)?.labelLocale;
};
